// N38 · Phase 3 — 打卡助手：右侧抽屉聊天（拍板 2026-06-06：抽屉全高 + 会话内保留历史）
// 你一句、助手一句；确认/追问/纠正都是气泡 + 输入框上方的快捷操作区（IM quick-reply 式）。
// TTS 朗读助手台词（默认开，🔊⇄🔇 持久化）；台词模板在 checkin/assistantLines.js。
// 沿用 Phase 1/2 拍板：落库永远真实今天；冲突提示+确认即覆盖；生词教一次永久记住；
// [改] 纠正映射同步别名表。语音输入外包讯飞/系统输入法（D6），这里只接文字。
import React from 'react';
import { LineIcon } from './icons.jsx';
import { createPortal } from 'react-dom';
import { dateKey, addDays } from '../data/slots.js';
import { CATEGORIES, CAT_BY_ID } from '../data/categories.js';
import { planCheckin, retargetPlan, planDirect } from '../checkin/checkinPlan.js';
import { makeRange } from '../checkin/timeParser.js';
import { loadAliases, learnAlias } from '../checkin/aliasStore.js';
import { lines } from '../checkin/assistantLines.js';
import { speak, isTtsMuted, setTtsMuted } from '../checkin/tts.js';
import { recommendNext, findPrevActivity, currentSlotBounds } from '../checkin/schedule.js';

const PLACEHOLDER = '说一句话打卡，回车发送';

export function CheckinTool({ data, onApply }) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [msgs, setMsgs] = React.useState([]); // {role:'user'|'bot', text} —— 会话内保留，刷新清空
  // pending：等用户操作的快捷区。{mode, plan?, word?, src}（src=原句，教完重解析用）
  // mode：confirm 确认 / ask 生词追问 / fixmenu 改哪里 / fix 改分类 / editnote 改备注
  const [pending, setPending] = React.useState(null);
  const [noteDraft, setNoteDraft] = React.useState(''); // editnote 模式的备注草稿
  const [muted, setMuted] = React.useState(isTtsMuted());
  const [recs, setRecs] = React.useState([]); // Phase 4 推荐：剧本算出的下一步分类 id 列表
  const listRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const greetedRef = React.useRef(false);
  const lastSuggestKeyRef = React.useRef(''); // 同一「格+上一件事」只播报一次，防重开抽屉刷屏

  // kind：'done'=落库成功（绿色强调气泡，一眼区分于普通对话）
  function say(t, kind) {
    setMsgs(m => [...m, { role: 'bot', text: t, kind }]);
    speak(t);
  }

  // Phase 4 推荐流：按「当前时段 + 上一件已打卡事」算下一步；同一情形只播报一次台词。
  // prevOverride：刚落库时传 plan.catId 当上一件事（setData 异步，此刻读 data 还是旧的）
  function refreshRecs(announce, prevOverride) {
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    let prevCat = prevOverride;
    if (prevCat === undefined) {
      const p = findPrevActivity(data[dateKey(now)], nowMin);
      prevCat = p ? p.catId : null;
    }
    const list = recommendNext(nowMin, prevCat)
      .filter(id => CAT_BY_ID[id]); // 剧本里写了不存在的分类（手改/分类被删）就跳过
    setRecs(list);
    if (!announce || list.length === 0) return;
    const slot = currentSlotBounds(nowMin);
    const key = `${slot ? slot.idx : -1}:${prevCat || ''}:${list.join()}`;
    if (key === lastSuggestKeyRef.current) return;
    lastSuggestKeyRef.current = key;
    const prevName = prevCat && CAT_BY_ID[prevCat] ? (CAT_BY_ID[prevCat].name || CAT_BY_ID[prevCat].label) : null;
    say(lines.suggest(prevName, list.map(id => CAT_BY_ID[id].name || CAT_BY_ID[id].label)));
  }

  // 抽屉开关：挤压主区域（CSS 看 body.checkin-drawer-open）+ 首次打开问候 + 推荐 + 聚焦输入框
  React.useEffect(() => {
    document.body.classList.toggle('checkin-drawer-open', open);
    if (open && !greetedRef.current) { greetedRef.current = true; say(lines.greet()); }
    if (open) refreshRecs(true);
    if (open && inputRef.current) inputRef.current.focus();
    return () => document.body.classList.remove('checkin-drawer-open');
  }, [open]);

  // 新气泡 / 快捷区变化 → 滚到底
  React.useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs, pending]);

  function runPlan(input) {
    const now = new Date();
    return planCheckin(input, {
      nowMin: now.getHours() * 60 + now.getMinutes(),
      todayKey: dateKey(now),
      nextDayKey: dateKey(addDays(now, 1)),
      getDay: (k) => data[k],
      extraAliases: loadAliases(),
    });
  }

  function submit() {
    const input = text.trim();
    if (!input) return;
    setMsgs(m => [...m, { role: 'user', text: input }]);
    setText('');
    setPending(null); // 没处理完的旧确认作废，按新句子来
    const plan = runPlan(input);
    if (plan.ok) {
      setPending({ mode: 'confirm', plan, src: input });
      say(lines.confirm(plan));
      return;
    }
    if (plan.reason === 'activity' && plan.unknownWord) {
      setPending({ mode: 'ask', word: plan.unknownWord, src: input });
      say(lines.ask(plan.unknownWord));
      return;
    }
    say(plan.reason === 'time' ? lines.failTime() : lines.failActivity());
  }

  // 追问/[改]选了分类：word→catId 进别名表，原句重解析 → 确认
  function teach(word, catId) {
    learnAlias(word, catId);
    const catName = (CAT_BY_ID[catId] && CAT_BY_ID[catId].name) || catId;
    const plan = runPlan(pending.src);
    if (!plan.ok) { setPending(null); say(lines.reparseFail()); return; }
    setPending({ mode: 'confirm', plan, src: pending.src });
    say(`${lines.learned(word, catName)} ${lines.confirm(plan)}`);
  }

  // 改分类：直接在当前 plan 上打补丁（选什么记什么），不重解析原句——重解析会丢手改的
  // 备注、还可能匹配回别的词（B14：嘴上说怦然落库成预备）。别名照常学，下次直接认。
  function fix(catId) {
    if (catId === pending.plan.catId) { setPending({ ...pending, mode: 'confirm' }); return; }
    const alias = pending.plan.matchedAlias;
    if (alias) learnAlias(alias, catId);
    const plan = retargetPlan(pending.plan, catId, { getDay: (k) => data[k] });
    setPending({ mode: 'confirm', plan, src: pending.src });
    say(`${alias ? `${lines.learned(alias, plan.catName)} ` : ''}${lines.confirm(plan)}`);
  }

  // 改备注：错别字/多字少字直接编辑，改完回确认（落库走的就是 plan.note）
  function submitNote() {
    const v = noteDraft.trim() || null;
    const plan = { ...pending.plan, note: v };
    setPending({ mode: 'confirm', plan, src: pending.src });
    say(lines.confirm(plan));
  }

  // 落库 + done 气泡 + 链式推荐下一步（确认流和推荐一键打卡共用）
  function applyAndDone(p) {
    onApply(p);
    // done 气泡走结构化渲染（标签/正文/备注三层），text 仍是完整句子（TTS 念这个）
    setMsgs(m => [...m, {
      role: 'bot', kind: 'done', text: lines.done(p),
      done: { timeLabel: p.timeLabel, catId: p.catId, catName: p.catName, note: p.note },
    }]);
    speak(lines.done(p));
    setPending(null);
    refreshRecs(true, p.catId);
  }

  function confirm() {
    applyAndDone(pending.plan);
  }

  // 推荐一键打卡：记当前所在格（单点占一格口径）。无冲突直接落库；有冲突走确认
  function pickRec(catId) {
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const slot = currentSlotBounds(nowMin);
    if (!slot) return;
    const time = makeRange(slot.startMin, slot.endMin, 'point', '');
    const plan = planDirect(catId, time, {
      todayKey: dateKey(now),
      nextDayKey: dateKey(addDays(now, 1)),
      getDay: (k) => data[k],
    });
    if (plan.conflicts.count > 0) {
      setPending({ mode: 'confirm', plan, src: '' });
      say(lines.confirm(plan));
      return;
    }
    applyAndDone(plan);
  }

  function cancel() {
    say(lines.cancel());
    setPending(null);
  }

  function toggleMute() {
    const next = !muted;
    setMuted(next);
    setTtsMuted(next);
    if (!next) speak(lines.ttsOn()); // 开声立刻说一句，当场确认 TTS 活着
  }

  function renderCatGrid(onPick) {
    return (
      <div className="checkin-cat-grid">
        {CATEGORIES.map(c => (
          <button key={c.id} type="button" className="checkin-cat-chip" onClick={() => onPick(c.id)}>
            <span className="checkin-dot" style={{ background: c.color }} />
            <span className="checkin-cat-chip-name">{c.name || c.label}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="checkin-wrap">
      <button className="icon-btn" title="打卡助手（说一句话→时间块）" onClick={() => setOpen(o => !o)}>
        <LineIcon name="chat" /><span className="icon-btn-text">打卡</span>
      </button>
      {/* portal 到 body：app-header 是 sticky+z-index:50 的堆叠上下文，抽屉留在里面的话
          z-index 对外只算 50，会被移动端 z:55 的底部分类面板盖住输入框 */}
      {open && createPortal(
        <div className="checkin-drawer">
          <div className="checkin-drawer-head">
            <span className="checkin-drawer-title">打卡助手</span>
            <button type="button" className="icon-btn" title={muted ? '打开朗读' : '关闭朗读'} onClick={toggleMute}>
              {muted ? '🔇' : '🔊'}
            </button>
            <button type="button" className="icon-btn" title="收起" onClick={() => setOpen(false)}>✕</button>
          </div>
          <div className="checkin-msgs" ref={listRef}>
            {msgs.map((m, i) => (
              m.kind === 'done' && m.done ? (
                <div key={i} className="checkin-bubble bot done">
                  <div className="checkin-done-label">
                    <span className="checkin-done-badge">✓</span>已记上
                    <button type="button" className="checkin-bubble-play" title="听这一条" onClick={() => speak(m.text, true)}>🔊</button>
                  </div>
                  <div className="checkin-done-main">
                    <span className="checkin-dot" style={{ background: (CAT_BY_ID[m.done.catId] && CAT_BY_ID[m.done.catId].color) || 'var(--accent)' }} />
                    今天 {m.done.timeLabel} · {m.done.catName}
                  </div>
                  {m.done.note && <div className="checkin-done-note">📝 {m.done.note}</div>}
                </div>
              ) : m.role === 'user' ? (
                <div key={i} className="checkin-bubble user">{m.text}</div>
              ) : (
                <div key={i} className="checkin-bubble bot">
                  {m.text}
                  <button type="button" className="checkin-bubble-play" title="听这一条" onClick={() => speak(m.text, true)}>🔊</button>
                </div>
              )
            ))}
          </div>
          {pending && (
            <div className="checkin-actions">
              {pending.mode === 'confirm' && (
                <div className="checkin-actions-row">
                  <button type="button" className="btn primary" onClick={confirm}>✓ 对，记上</button>
                  <button type="button" className="btn" onClick={() => { setPending({ ...pending, mode: 'fixmenu' }); say(lines.fixWhat()); }}>改</button>
                  <button type="button" className="btn" onClick={cancel}>✕ 算了</button>
                </div>
              )}
              {pending.mode === 'fixmenu' && (
                <div className="checkin-actions-row">
                  <button type="button" className="btn" onClick={() => { setPending({ ...pending, mode: 'fix' }); say(lines.fixAsk(pending.plan.matchedAlias)); }}>改分类</button>
                  <button type="button" className="btn" onClick={() => { setNoteDraft(pending.plan.note || ''); setPending({ ...pending, mode: 'editnote' }); say(lines.fixNoteAsk(pending.plan.note)); }}>
                    {pending.plan.note ? '改备注' : '加备注'}
                  </button>
                  <button type="button" className="btn" onClick={() => setPending({ ...pending, mode: 'confirm' })}>‹ 返回</button>
                </div>
              )}
              {pending.mode === 'editnote' && (
                <React.Fragment>
                  <input
                    className="checkin-input"
                    type="text"
                    autoFocus
                    value={noteDraft}
                    placeholder="备注内容，清空 = 不要备注"
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitNote(); }}
                  />
                  <div className="checkin-actions-row">
                    <button type="button" className="btn primary" onClick={submitNote}>✓ 好了</button>
                    <button type="button" className="btn" onClick={() => setPending({ ...pending, mode: 'confirm' })}>‹ 返回</button>
                  </div>
                </React.Fragment>
              )}
              {pending.mode === 'ask' && (
                <React.Fragment>
                  {renderCatGrid((catId) => teach(pending.word, catId))}
                  <div className="checkin-actions-row">
                    <button type="button" className="btn" onClick={cancel}>✕ 算了</button>
                  </div>
                </React.Fragment>
              )}
              {pending.mode === 'fix' && (
                <React.Fragment>
                  {renderCatGrid(fix)}
                  <div className="checkin-actions-row">
                    <button type="button" className="btn" onClick={() => setPending({ ...pending, mode: 'confirm' })}>‹ 返回</button>
                  </div>
                </React.Fragment>
              )}
            </div>
          )}
          {!pending && recs.length > 0 && (
            <div className="checkin-actions checkin-recs">
              <div className="checkin-recs-label">接下来可以（点了记到当前时段）：</div>
              <div className="checkin-recs-row">
                {recs.map(id => (
                  <button key={id} type="button" className="checkin-cat-chip" onClick={() => pickRec(id)}>
                    <span className="checkin-dot" style={{ background: CAT_BY_ID[id].color }} />
                    <span className="checkin-cat-chip-name">{CAT_BY_ID[id].name || CAT_BY_ID[id].label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="checkin-inputbar">
            <input
              ref={inputRef}
              className="checkin-input"
              type="text"
              value={text}
              placeholder={PLACEHOLDER}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
